import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface CallHistoryItem {
  id: string;
  direction: string;
  status: string | null;
  from_e164: string | null;
  to_e164: string | null;
  created_at: string;
  duration_seconds: number | null;
  contact_id: string | null;
  hasRecording: boolean;
  contacts?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    primary_phone: string | null;
  } | null;
  callType: 'missed' | 'incoming' | 'outgoing';
  displayNumber: string;
  displayName: string;
}

export function useCallHistory() {
  const [callHistory, setCallHistory] = useState<CallHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  const loadCallHistory = async () => {
    try {
      setLoading(true);
      
      // Get current user's app_user_id
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: appUser } = await supabase
        .from('app_users')
        .select('id, twilio_phone_number')
        .eq('auth_user_id', user.id)
        .single();

      if (!appUser) return;

      // Build filter: handled_by_app_user_id OR created_by (auth UUID)
      // Also include calls from the user's Twilio phone number as fallback
      let orFilter = `handled_by_app_user_id.eq.${appUser.id},created_by.eq.${appUser.id}`;
      if (appUser.twilio_phone_number) {
        orFilter += `,from_e164.eq.${appUser.twilio_phone_number}`;
      }

      // Fetch last 50 calls for this user
      const { data: calls, error } = await supabase
        .from('calls')
        .select(`
          id,
          direction,
          status,
          from_e164,
          to_e164,
          created_at,
          duration_seconds,
          contact_id,
          contact:contacts!contact_calls_contact_id_fkey (
            id,
            first_name,
            last_name,
            primary_phone
          ),
          call_recordings!left (
            id,
            duration_seconds
          )
        `)
        .or(orFilter)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      // Process and categorize calls
      const processedCalls = calls?.map(call => {
        let callType: 'missed' | 'incoming' | 'outgoing' = 'outgoing';
        
        if (call.direction === 'inbound') {
          if (['no-answer', 'busy', 'failed', 'canceled'].includes(call.status || '')) {
            callType = 'missed';
          } else if (call.status === 'completed') {
            callType = 'incoming';
          }
        }

        // Determine display name and number
        let displayName = '';
        let displayNumber = '';

        const contactData = (call as any).contact;
        if (contactData) {
          displayName = [contactData.first_name, contactData.last_name]
            .filter(Boolean)
            .join(' ');
          displayNumber = contactData.primary_phone || '';
        }

        // If no name, use the phone number
        if (!displayName) {
          displayNumber = call.direction === 'inbound' 
            ? call.from_e164 || '' 
            : call.to_e164 || '';
          displayName = displayNumber;
        }

        const hasRecording = Array.isArray(call.call_recordings) && call.call_recordings.length > 0;
        const maxRecDuration = hasRecording 
          ? (call.call_recordings as any[]).reduce((max: number, r: any) => Math.max(max, r.duration_seconds || 0), 0) 
          : 0;
        const effectiveDuration = Math.max(call.duration_seconds || 0, maxRecDuration);

        return {
          ...call,
          duration_seconds: effectiveDuration,
          callType,
          displayName,
          displayNumber: displayNumber || call.to_e164 || call.from_e164 || '',
          hasRecording,
        };
      }) || [];

      setCallHistory(processedCalls);
    } catch (error) {
      console.error('Error loading call history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCallHistory();

    // Set up real-time subscription
    const channel = supabase
      .channel('call-history-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'calls'
        },
        () => {
          loadCallHistory();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return { callHistory, loading };
}
